import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
//
import { getDefaultPaymentCardOrFirst } from '../../reducers/user/userReducer';
import paymentCategoryConstants from '../../../constants/paymentCategoryConstants';
import { DETERMINE_SELECTED_PAYMENT_TYPE } from '../../../constants/actionConstants';
import paymentTypeConstants from '../../../constants/paymentTypeConstants';
import featureConstants from '../../../constants/featureConstants';
import userConstants from '../../../constants/userConstants';
import { setSelectedPaymentCategory } from './paymentActions';
import isBasketPayIn3Eligible from '../../../utils/payment/isBasketPayIn3Eligible';
import isBasketClearpayEligible from '../../../utils/payment/isBasketClearpayEligible';
import isPayPalAvailable from '../../../utils/payment/isPayPalAvailable';
import { isFeatureActive } from '../../reducers/config/configReducer';

export function canSelectGooglePay(payment) {
  return payment.canMakeGooglePayPayments && payment.googlePayPaymentsClientHasBeenCreated;
}

export function posCreditNoLongerEligible(posCreditEligible, selectedPaymentType) {
  return !posCreditEligible && includes(selectedPaymentType, paymentTypeConstants.POS_CREDIT);
}

export function payPalPayIn3NoLongerEligible(payPalPayIn3Eligible, selectedPaymentType) {
  return !payPalPayIn3Eligible && includes(selectedPaymentType, paymentTypeConstants.PAYPAL_PAY_IN_3);
}

export function clearpayNoLongerEligible(clearpayEligible, selectedPaymentType) {
  return !clearpayEligible && includes(selectedPaymentType, paymentTypeConstants.CLEARPAY);
}

export function getAvailablePayLaterOptions(state = {}) {
  const availableOptions = [];

  if (isBasketPayIn3Eligible(state)) availableOptions.push(paymentTypeConstants.PAYPAL_PAY_IN_3);
  if (isBasketClearpayEligible(state)) availableOptions.push(paymentTypeConstants.CLEARPAY);
  if (
    state.payment?.posCreditEligible &&
    !isEmpty(state.payment?.creditTypeGroups)
  ) availableOptions.push(paymentTypeConstants.POS_CREDIT);

  return availableOptions;
}

export const checkPayLaterOptionsAvailable = () => (dispatch, getState) => {
  const {
    selectedPaymentType,
    selectedPaymentCategory,
    posCreditEligible,
  } = getState().payment;

  if (selectedPaymentCategory === paymentCategoryConstants.PAY_LATER) {
    // check if selected pay later option is still available and reset to PAY_NOW if not
    const payPalPayIn3Eligible = isBasketPayIn3Eligible(getState());
    const clearpayEligible = isBasketClearpayEligible(getState());

    const selectNewPaymentType =
      posCreditNoLongerEligible(posCreditEligible, selectedPaymentType) ||
      payPalPayIn3NoLongerEligible(payPalPayIn3Eligible, selectedPaymentType) ||
      clearpayNoLongerEligible(clearpayEligible, selectedPaymentType);

    if (selectNewPaymentType) {
      return dispatch(setSelectedPaymentCategory(paymentCategoryConstants.PAY_NOW, false));
    }
  }

  return {};
};

export function getPayNowPriorityOrder(enablePaymentPreference, paymentMethodPreference) {
  const {
    APPLE_PAY,
    GOOGLE_PAY,
    SAVED_PAYMENT_CARD,
    CREDIT_CARD,
    PAYPAL,
  } = paymentTypeConstants;

  const priorityOrder = [APPLE_PAY, GOOGLE_PAY, SAVED_PAYMENT_CARD, CREDIT_CARD, PAYPAL];

  if (enablePaymentPreference) {
    const {
      PAYMENT_PREFERENCE_PAYPAL,
      PAYMENT_PREFERENCE_APPLE_PAY,
      PAYMENT_PREFERENCE_GOOGLE_PAY,
      PAYMENT_PREFERENCE_SAVED_CARD,
    } = userConstants;

    if ([
      PAYMENT_PREFERENCE_PAYPAL,
      PAYMENT_PREFERENCE_APPLE_PAY,
      PAYMENT_PREFERENCE_GOOGLE_PAY,
      PAYMENT_PREFERENCE_SAVED_CARD,
    ].includes(paymentMethodPreference)) {
      const lookup = new Map();
      lookup.set(PAYMENT_PREFERENCE_APPLE_PAY, APPLE_PAY);
      lookup.set(PAYMENT_PREFERENCE_GOOGLE_PAY, GOOGLE_PAY);
      lookup.set(PAYMENT_PREFERENCE_SAVED_CARD, SAVED_PAYMENT_CARD);
      lookup.set(PAYMENT_PREFERENCE_PAYPAL, PAYPAL);

      // move the preferred payment method to the front of the list
      const mappedPreference = lookup.get(paymentMethodPreference);
      const found = priorityOrder.indexOf(mappedPreference);
      priorityOrder.unshift(priorityOrder.splice(found, 1)[0]);
    }
  }

  return priorityOrder;
}

export const determineSelectedPaymentType = () => async (dispatch, getState) => {
  await dispatch(checkPayLaterOptionsAvailable());

  const {
    applePayCompatibilityChecksComplete,
    selectedPaymentType,
    paymentWallet = [],
    creditTypeGroups,
  } = getState().payment;

  if (!selectedPaymentType) {
    let newSelectedPaymentType;

    const defaultSavedPaymentCard = getDefaultPaymentCardOrFirst(paymentWallet);
    const selectedPaymentCategory = getState().payment.selectedPaymentCategory;

    if (!selectedPaymentCategory || selectedPaymentCategory === paymentCategoryConstants.PAY_NOW) {
      const {
        APPLE_PAY,
        GOOGLE_PAY,
        SAVED_PAYMENT_CARD,
        CREDIT_CARD,
        PAYPAL,
      } = paymentTypeConstants;

      const priorityOrder = getPayNowPriorityOrder(
        isFeatureActive(getState(), featureConstants.ENABLE_PAYMENT_PREFERENCE),
        getState().user?.customerPreferences?.paymentChoice,
      );

      priorityOrder.forEach(((method) => {
        // Try and select each payment method in turn, setting newSelectedPaymentType if any succeed to stop trying others
        if (!newSelectedPaymentType) {
          if (method === APPLE_PAY && applePayCompatibilityChecksComplete) newSelectedPaymentType = method;
          if (method === GOOGLE_PAY && canSelectGooglePay(getState().payment)) newSelectedPaymentType = method;
          if (method === SAVED_PAYMENT_CARD && defaultSavedPaymentCard) newSelectedPaymentType = `${SAVED_PAYMENT_CARD}:${defaultSavedPaymentCard.id}`;
          if (method === CREDIT_CARD && isEmpty(paymentWallet)) newSelectedPaymentType = CREDIT_CARD;
          if (method === PAYPAL && isPayPalAvailable(getState())) newSelectedPaymentType = method;
        }
      }));
    }

    if (selectedPaymentCategory === paymentCategoryConstants.PAY_LATER) {
      const payLaterOptionsAvailable = getAvailablePayLaterOptions(getState());

      if (payLaterOptionsAvailable.length === 1) {
        // Only select a pay later option when there's only one option available
        const option = payLaterOptionsAvailable[0];
        const {
          CLEARPAY,
          POS_CREDIT,
          PAYPAL_PAY_IN_3,
        } = paymentTypeConstants;

        if (option === POS_CREDIT && creditTypeGroups.length === 1) newSelectedPaymentType = `${POS_CREDIT}:${creditTypeGroups[0]?.id}`;
        if (option === PAYPAL_PAY_IN_3) newSelectedPaymentType = PAYPAL_PAY_IN_3;
        if (option === CLEARPAY) newSelectedPaymentType = CLEARPAY;
      }
    }

    if (newSelectedPaymentType) {
      dispatch({
        type: DETERMINE_SELECTED_PAYMENT_TYPE,
        selectedPaymentType: newSelectedPaymentType,
      });
    }
  }
};

export default determineSelectedPaymentType;
